import { successors, type FlowGraph } from '@cartly/shared';

export const NODE_WIDTH = 248;
export const NODE_HEIGHT = 72;
const GAP_X = 40;
const GAP_Y = 56;

export interface Position {
  x: number;
  y: number;
}

/**
 * Deterministic top-down layout: a step sits one row below its deepest predecessor, steps in a
 * row are ordered by first visit (yes before no), and steps that cannot be reached from the
 * start are parked in a final row. Positions are not stored, so a flow always looks the same.
 */
export function layoutFlow(graph: FlowGraph): Map<string, Position> {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const preorder: string[] = [];
  const post: string[] = [];
  const state = new Map<string, 1 | 2>();
  const visit = (id: string) => {
    state.set(id, 1);
    preorder.push(id);
    for (const t of successors(byId.get(id)!)) {
      if (byId.has(t) && !state.has(t)) visit(t);
    }
    state.set(id, 2);
    post.push(id);
  };
  if (graph.start !== null && byId.has(graph.start)) visit(graph.start);

  const order = [...post].reverse();
  const rank = new Map(order.map((id, i) => [id, i]));
  const depth = new Map<string, number>();
  if (order.length > 0) depth.set(order[0]!, 0);
  for (const id of order) {
    const d = depth.get(id) ?? 0;
    for (const t of successors(byId.get(id)!)) {
      // Back edges (cycles in a draft) point to an earlier rank and are ignored.
      if (rank.has(t) && rank.get(t)! > rank.get(id)!) {
        depth.set(t, Math.max(depth.get(t) ?? 0, d + 1));
      }
    }
  }

  const rows = new Map<number, string[]>();
  for (const id of preorder) {
    const d = depth.get(id) ?? 0;
    rows.set(d, [...(rows.get(d) ?? []), id]);
  }
  const loose = graph.nodes.map((n) => n.id).filter((id) => !state.has(id));
  const deepest = Math.max(-1, ...rows.keys());
  if (loose.length > 0) rows.set(deepest + 1, loose);

  const out = new Map<string, Position>();
  for (const [d, ids] of rows) {
    ids.forEach((id, i) => {
      out.set(id, {
        x: (i - (ids.length - 1) / 2) * (NODE_WIDTH + GAP_X),
        // Row 0 is reserved for the trigger above the start.
        y: (d + 1) * (NODE_HEIGHT + GAP_Y),
      });
    });
  }
  return out;
}

/** Where the trigger card goes: centred above the first row. */
export const TRIGGER_POSITION: Position = { x: 0, y: 0 };
